'use client';

import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { runnerTypeValues, type Daemon, type DaemonShare, type OsType, type RunnerType, type Team } from '../lib/api';
import { resolveApiErrorToastMessageOrFallback } from '../lib/api/localizeApiError';
import { isQuotaExceededError } from '../lib/api/quotaError';
import {
  useDaemons,
  useCreateDaemonMutation,
  useCreateDaemonShareMutation,
  useDaemonShareCandidates,
  useDaemonShares,
  useDeleteDaemonMutation,
  useRevokeDaemonShareMutation,
  useUpdateDaemonMutation,
  useUpdateDaemonShareMutation,
  useRequestDaemonRestartMutation,
} from '../hooks/queries/useDaemons';
import { BaseModal } from './BaseModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { formatLocalDateTime } from '../lib/dateTime';
import { copyTextToClipboard } from '../lib/clipboard';
import { isDaemonOnline, DEFAULT_DAEMON_ONLINE_THRESHOLD_MS } from '../lib/daemonOnline';
import { isDetectedEnginesFresh, resolveEffectiveEngines, type DaemonEngineSources } from '../lib/daemonEngines';
import { cn } from '../lib/cn';
import {
  isSafeInstallVersion,
  shouldShowPendingRunnerVersion,
  shouldShowRunnerUpdateFailure,
} from '../lib/runnerVersion';
import { Select, type SelectOption } from './Select';
import { Spinner } from './loading/Spinner';
import { Tooltip } from './Tooltip';
import { useShareableTeamProjects } from '../hooks/queries/useTeams';

type DaemonManagerPanelProps = {
  token: string | null;
  teams: Team[];
  currentTeamId: string;
  /** 화면에 보이는 동안에만 폴링/상태 갱신을 수행한다. 모달은 열림 여부, 페이지는 항상 true. */
  isActive?: boolean;
};

type DaemonCreateModalProps = {
  isOpen: boolean;
  token: string | null;
  daemon: Daemon | null;
  onClose: () => void;
};

type SortableDaemonListItemProps = {
  daemon: Daemon;
  isDisabled: boolean;
  isDraggingActive: boolean;
  dragLabel: string;
  children: ReactNode;
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return '-';
  return formatLocalDateTime(dateStr);
};

const formatRunnerVersion = (runnerVersion: string | null): string => {
  if (!runnerVersion) return '-';
  return runnerVersion.startsWith('v') ? runnerVersion : `v${runnerVersion}`;
};

const buildInstallScript = (token: string): string =>
  `npm install -g @agentteams/runner\nagentrunner init --token ${token}`;

/** 자동 업데이트 실패는 패키지 단위로 보고된다. 안내 명령도 실패한 패키지에 맞춰야 한다. */
const runnerUpdatePackageNames = {
  cli: '@agentteams/cli',
  runner: '@agentteams/runner',
} as const;

/** 자동 업데이트가 막혔을 때 사용자가 직접 실행할 수 있는 설치 명령. */
const buildManualUpdateCommand = (packageName: string, version: string): string =>
  `npm install -g ${packageName}@${version}`;

const daemonManageRefetchIntervalMs = 30_000;
const daemonStatusTickMs = 15_000;
const activeRunnerTypeSet = new Set<string>(runnerTypeValues);

const sanitizeSupportedEngines = (supportedEngines: readonly string[]): RunnerType[] =>
  supportedEngines.filter((engine): engine is RunnerType => activeRunnerTypeSet.has(engine));

/// 공유 허용 엔진 후보는 러너의 유효 엔진(수동 지정 → 신선한 자동 탐지)으로 제한한다(빈 배열 = 전체 지원).
/// 서버도 저장 시 같은 교집합으로 정규화하므로 후보 밖 엔진은 UI에서 애초에 노출하지 않는다.
const getShareEngineCandidates = (daemon: DaemonEngineSources | null): RunnerType[] => {
  const effective = sanitizeSupportedEngines(daemon ? resolveEffectiveEngines(daemon) : []);
  return effective.length > 0 ? effective : [...runnerTypeValues];
};

const shareEngineBadgeVisibleCount = 2;

const runnerTypeIconConfig: Record<RunnerType, { src: string; mono?: boolean }> = {
  CLAUDE_CODE: { src: '/agents/claude.svg' },
  CODEX: { src: '/agents/openai.svg', mono: true },
  ANTIGRAVITY: { src: '/agents/antigravity.svg' },
  OPENCODE: { src: '/agents/opencode.svg', mono: true },
  AMP: { src: '/agents/ampcode.svg', mono: true },
  COPILOT_CLI: { src: '/agents/copilot.svg', mono: true },
  CURSOR_CLI: { src: '/agents/cursor.svg' },
  KIMI_CLI: { src: '/agents/kimi.svg' },
  KIRO_CLI: { src: '/agents/kiro.svg' },
  GROK_BUILD: { src: '/agents/grok.svg', mono: true },
  OMP: { src: '/agents/omp.svg', mono: true },
  MUSE_CODE: { src: '/agents/muse.svg', mono: true },
};

const getOsTypeIcon = (osType: OsType | '' | null | undefined) => {
  if (osType === 'MACOS') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M16.2 12.5c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-2-1.5-.2-2.8.9-3.6.9s-2-.9-3.2-.9c-1.7 0-3.2 1-4.1 2.5-1.8 3.1-.4 7.8 1.3 10.2.8 1.2 1.8 2.5 3.1 2.5 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.8 1.3-2.9-.1 0-2.7-1.1-2.8-4.2Z" />
        <path d="M13.8 5.3c.7-.9 1.2-2.1 1-3.3-1 .1-2.2.7-2.9 1.6-.6.8-1.2 2-1 3.2 1.1.1 2.2-.6 2.9-1.5Z" />
      </svg>
    );
  }

  if (osType === 'WINDOWS') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M3 5.2 10.7 4v7H3V5.2Zm8.8-1.3L21 2.5V11h-9.2V3.9ZM3 12.9h7.7v7L3 18.8v-5.9Zm8.8 0H21v8.5l-9.2-1.3v-7.2Z" />
      </svg>
    );
  }

  if (osType === 'LINUX') {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M12 2.5c-2.2 0-4 2.3-4 5.1 0 1.4.4 2.8 1.1 3.7-.6.6-1.1 1.6-1.1 2.9 0 .7.2 1.3.4 1.9l-1.9 1.2c-.7.4-1 1.2-.7 1.9l.3.8c.2.6.9 1 1.6.8l2.6-.7.6 1.3c.2.5.8.8 1.3.8s1-.3 1.3-.8l.6-1.3 2.6.7c.7.2 1.4-.2 1.6-.8l.3-.8c.3-.7 0-1.5-.7-1.9l-1.9-1.2c.3-.6.4-1.2.4-1.9 0-1.3-.5-2.3-1.1-2.9.7-.9 1.1-2.3 1.1-3.7 0-2.8-1.8-5.1-4-5.1Zm-1.6 5.1c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm3.2 0c.4 0 .7.4.7.8s-.3.8-.7.8-.7-.4-.7-.8.3-.8.7-.8Zm-3.1 3.4h3c-.1.8-.7 1.4-1.5 1.4s-1.4-.6-1.5-1.4Z" />
      </svg>
    );
  }

  return null;
};

const DragHandleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-3.5 w-3.5"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M7 4.5A1.5 1.5 0 1 1 4 4.5 1.5 1.5 0 0 1 7 4.5ZM16 4.5A1.5 1.5 0 1 1 13 4.5 1.5 1.5 0 0 1 16 4.5ZM7 10A1.5 1.5 0 1 1 4 10a1.5 1.5 0 0 1 3 0ZM16 10a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM7 15.5A1.5 1.5 0 1 1 4 15.5a1.5 1.5 0 0 1 3 0ZM16 15.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
  </svg>
);

const RunnerEngineIcons = ({ engines }: { engines: RunnerType[] }) => {
  const t = useTranslations('components.daemonManageModal');

  if (engines.length === 0) {
    return (
      <span
        className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-dashed border-base-300 px-1.5 text-xs text-[var(--color-fg-muted)]"
        aria-label={t('supportedEnginesEmpty')}
      >
        -
      </span>
    );
  }

  // 엔진이 많으면 한 덩어리로 카드 폭을 넘겨 페이지가 가로 스크롤되므로, 묶음 안에서도 줄바꿈을 허용한다.
  return (
    <span className="inline-flex min-w-0 flex-wrap items-center gap-1" aria-label={t('supportedEnginesValue')}>
      {engines.map((engine) => {
        const icon = runnerTypeIconConfig[engine];
        const label = t(`runnerTypeOption.${engine}`);

        return (
          <Tooltip
            key={engine}
            content={label}
            placement="top"
            focusable
            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-base-300 bg-base-100"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- runner icons are small runtime-selected assets. */}
            <img
              src={icon.src}
              alt=""
              className={cn('h-3.5 w-3.5 object-contain', icon.mono && 'dark:invert')}
              aria-hidden="true"
            />
          </Tooltip>
        );
      })}
    </span>
  );
};

const SortableDaemonListItem = ({
  daemon,
  isDisabled,
  isDraggingActive,
  dragLabel,
  children,
}: SortableDaemonListItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: daemon.id,
    disabled: isDisabled,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-xl border border-base-300 bg-base-100 p-3',
        isDragging && 'opacity-60 shadow-lg',
        isDraggingActive && !isDragging && 'transition-colors',
      )}
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex h-5 w-5 shrink-0 cursor-grab items-center justify-center rounded text-[var(--color-fg-muted)] transition-colors hover:bg-base-200 hover:text-base-content active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-40"
          aria-label={dragLabel}
          disabled={isDisabled}
          {...attributes}
          {...listeners}
        >
          <DragHandleIcon />
        </button>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </li>
  );
};

export const DaemonCreateModal = ({ isOpen, token, daemon, onClose }: DaemonCreateModalProps) => {
  const t = useTranslations('components.daemonManageModal');
  const tRoot = useTranslations();
  const createMutation = useCreateDaemonMutation(token);
  const updateMutation = useUpdateDaemonMutation(token);
  const [issuedToken, setIssuedToken] = useState<string | null>(null);
  const [daemonLabel, setDaemonLabel] = useState('');
  const [selectedEngines, setSelectedEngines] = useState<RunnerType[]>([]);
  const isEditMode = daemon !== null;
  const hasDetectionReport = Boolean(daemon?.detectedEnginesAt);
  // 신선도가 지난 탐지 결과는 서버가 차단 근거로 쓰지 않는다(구버전 다운그레이드·장기 오프라인).
  const isDetectionStale = Boolean(daemon) && hasDetectionReport && !isDetectedEnginesFresh(daemon!);
  const detectedEngines = sanitizeSupportedEngines(daemon?.detectedEngines ?? []);
  const detectedEngineSet = new Set(detectedEngines);
  const canUseAutomaticDetection = !isEditMode || hasDetectionReport;

  useEffect(() => {
    if (isOpen && daemon) {
      setIssuedToken(null);
      setDaemonLabel(daemon.label ?? '');
      setSelectedEngines(sanitizeSupportedEngines(daemon.supportedEngines));
      return;
    }

    if (!isOpen) {
      setIssuedToken(null);
      setDaemonLabel('');
      setSelectedEngines([]);
    }
  }, [daemon, isOpen]);

  const daemonLabelLength = daemonLabel.trim().length;
  const canCreateDaemon =
    Boolean(token) &&
    daemonLabelLength >= 2 &&
    daemonLabelLength <= 25 &&
    (selectedEngines.length > 0 || canUseAutomaticDetection) &&
    !(isEditMode ? updateMutation.isPending : createMutation.isPending);
  // 토큰이 발급되면 폼을 숨기고 1회성 설치 스크립트(토큰)만 노출한다.
  const showTokenOnly = !isEditMode && Boolean(issuedToken);
  const visibleRunnerTypes = runnerTypeValues;

  const handleEngineToggle = (runnerType: RunnerType) => {
    setSelectedEngines((current) => {
      return current.includes(runnerType)
        ? current.filter((engine) => engine !== runnerType)
        : [...current, runnerType];
    });
  };

  const handleSubmit = async () => {
    try {
      const supportedEngines = sanitizeSupportedEngines(selectedEngines);

      if (daemon) {
        await updateMutation.mutateAsync({
          id: daemon.id,
          input: {
            label: daemonLabel.trim(),
            supportedEngines,
          },
        });
        toast.success(t('toastUpdated'));
        onClose();
        return;
      }

      const created = await createMutation.mutateAsync({
        label: daemonLabel.trim(),
        supportedEngines,
      });
      setIssuedToken(created.token);
      setDaemonLabel('');
      setSelectedEngines([]);
      toast.success(t('toastTokenIssued'));
      // 토큰은 생성 직후 1회만 노출되므로 모달을 닫지 않고 설치 스크립트를 보여준다.
      // (모달을 닫으면 useEffect의 !isOpen 분기가 issuedToken을 초기화해 토큰이 사라진다)
    } catch (error) {
      if (!isEditMode && isQuotaExceededError(error)) {
        toast.error(t('daemonQuotaExceeded'));
        return;
      }

      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        isEditMode
          ? tRoot('uiErrors.updateFailed', { target: tRoot('uiErrors.targets.runner') })
          : tRoot('uiErrors.createFailed', { target: tRoot('uiErrors.targets.runner') }),
      );
      if (message) toast.error(message);
    }
  };

  const handleCopyToken = async () => {
    if (!issuedToken) {
      return;
    }

    try {
      await copyTextToClipboard(buildInstallScript(issuedToken));
      toast.success(t('toastCopied'));
    } catch {
      toast.error(tRoot('uiErrors.copyFailed'));
    }
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} variant="bottomSheet" size="lg">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold">{isEditMode ? t('editTitle') : t('createTitle')}</h3>
        <button type="button" className="btn btn-ghost btn-circle btn-sm" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {showTokenOnly ? null : (
        <div className="rounded-xl border border-base-300 bg-base-200 p-3">
          <div className="mb-2 text-xs text-[var(--color-fg-muted)]">{t('tokenOneTimeNotice')}</div>
          <div className="space-y-3">
            <input
              type="text"
              className="input input-bordered input-sm w-full"
              value={daemonLabel}
              onChange={(event) => setDaemonLabel(event.target.value)}
              placeholder={t('labelPlaceholder')}
              minLength={2}
              maxLength={25}
            />
            {hasDetectionReport ? (
              <div className="rounded-lg border border-base-300 bg-base-100 p-3">
                <div className="text-xs font-medium">{t('detectedEnginesLabel')}</div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {detectedEngines.length > 0 ? (
                    detectedEngines.map((runnerType) => (
                      <span
                        key={runnerType}
                        className="rounded-full bg-[var(--chip-success-bg)] px-2 py-0.5 text-xs text-[var(--chip-success-fg)]"
                      >
                        {t(`runnerTypeOption.${runnerType}`)}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-[var(--color-fg-muted)]">{t('detectedEnginesEmpty')}</span>
                  )}
                </div>
                <div className="mt-2 text-xs text-[var(--color-fg-subtle)]">
                  {t('detectedEnginesAt', { value: formatDate(daemon?.detectedEnginesAt ?? null) })}
                </div>
                {/* 낡은 스냅샷은 서버 판정에서 제외되므로, 왜 후보가 좁혀지지 않는지 화면에서 알 수 있어야 한다. */}
                {isDetectionStale ? <div className="mt-1 text-xs text-warning">{t('detectedEnginesStale')}</div> : null}
              </div>
            ) : null}
            <fieldset className="space-y-2">
              <legend className="text-xs font-medium">
                {canUseAutomaticDetection ? t('manualEnginesLabel') : t('supportedEnginesLabel')}
              </legend>
              <div className="grid gap-2 sm:grid-cols-2">
                {visibleRunnerTypes.map((runnerType) => {
                  const checked = selectedEngines.includes(runnerType);
                  const detected = detectedEngineSet.has(runnerType);

                  return (
                    <label
                      key={runnerType}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-3 py-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={checked}
                        onChange={() => handleEngineToggle(runnerType)}
                      />
                      <span>{t(`runnerTypeOption.${runnerType}`)}</span>
                      {detected ? (
                        <span className="ml-auto rounded-full bg-[var(--chip-success-bg)] px-1.5 py-0.5 text-[10px] text-[var(--chip-success-fg)]">
                          {t('detectedBadge')}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              {canUseAutomaticDetection && selectedEngines.length === 0 ? (
                <div className="text-xs font-medium text-success">{t('manualEnginesAutomatic')}</div>
              ) : null}
              <div className="text-xs text-[var(--color-fg-muted)]">
                {canUseAutomaticDetection ? t('manualEnginesHint') : t('supportedEnginesHint')}
              </div>
            </fieldset>
            <button
              type="button"
              className="btn btn-primary btn-sm w-full"
              onClick={() => void handleSubmit()}
              disabled={!canCreateDaemon}
            >
              {isEditMode
                ? updateMutation.isPending
                  ? t('saving')
                  : t('saveChanges')
                : createMutation.isPending
                  ? t('creating')
                  : t('create')}
            </button>
          </div>
        </div>
      )}

      {!isEditMode && issuedToken ? (
        <div className="mt-4 rounded-xl border border-warning/40 bg-warning/10 p-3">
          <div className="mb-2 text-xs font-medium text-[var(--color-fg-muted)]">{t('installScript')}</div>
          <pre className="whitespace-pre-wrap break-all rounded-md bg-base-100 p-2 font-mono text-xs">
            {buildInstallScript(issuedToken)}
          </pre>
          <div className="mt-2 flex justify-end">
            <button type="button" className="btn btn-ghost btn-xs" onClick={() => void handleCopyToken()}>
              {t('copy')}
            </button>
          </div>
        </div>
      ) : null}
    </BaseModal>
  );
};

const DaemonRestartButton = ({ token, daemonId }: { token: string | null; daemonId: string }) => {
  const t = useTranslations('components.daemonManageModal');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const restartMutation = useRequestDaemonRestartMutation(token);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleRestart = async () => {
    try {
      await restartMutation.mutateAsync(daemonId);
      toast.success(t('toastRestartRequested'));
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('toastRestartFailed'));
      if (message) toast.error(message);
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-xs px-2"
        onClick={() => setIsConfirmOpen(true)}
        disabled={restartMutation.isPending}
      >
        {t('restart')}
      </button>
      <DeleteConfirmModal
        isOpen={isConfirmOpen}
        title={t('restartConfirmTitle')}
        message={t('restartConfirmMessage')}
        confirmLabel={t('restartConfirmLabel')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={restartMutation.isPending}
        onConfirm={() => void handleRestart()}
        onCancel={() => setIsConfirmOpen(false)}
      />
    </>
  );
};

type ShareWizardStep = 'target' | 'conditions' | 'agents' | 'preview' | 'result';

const shareWizardSteps: ShareWizardStep[] = ['target', 'conditions', 'agents', 'preview'];

/**
 * 러너 공유 설정 마법사.
 *
 * `팀/프로젝트 → 실행 조건(Plan Mode·허용 엔진) → 노출 에이전트 → 수신자 결과 미리보기` 순으로
 * 진행하고, 마지막에 공유 조건과 노출 에이전트를 **한 번의 API 호출**로 저장한다.
 * 노출 에이전트 0개도 유효한 선택이며(저장은 되지만 상대방 요청 목록에는 보이지 않음),
 * 저장 전·결과 화면에서 그 결과를 인라인으로 명시한다.
 */
export const DaemonShareModal = ({
  isOpen,
  token,
  daemon,
  teams,
  currentTeamId,
  shares,
  onClose,
}: {
  isOpen: boolean;
  token: string | null;
  daemon: Daemon | null;
  teams: Team[];
  currentTeamId: string;
  shares: DaemonShare[];
  onClose: () => void;
}) => {
  const t = useTranslations('components.daemonManageModal');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const createShareMutation = useCreateDaemonShareMutation(token);
  const revokeShareMutation = useRevokeDaemonShareMutation(token);
  const updateShareMutation = useUpdateDaemonShareMutation(token);
  const defaultShareTeamId = currentTeamId || teams[0]?.id || '';

  const [step, setStep] = useState<ShareWizardStep>('target');
  // 편집 중인 활성 공유. null이면 새 공유 생성 흐름이다.
  const [editingShare, setEditingShare] = useState<DaemonShare | null>(null);
  const [selectedShareTeamId, setSelectedShareTeamId] = useState(defaultShareTeamId);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [planModeOnly, setPlanModeOnly] = useState(false);
  // 'all' = 제한 없음(빈 배열 저장), 'selected' = 체크한 엔진만 허용.
  // 빈 배열이 서버에서 "제한 없음"으로 해석되므로 두 상태를 UI에서 명시적으로 구분한다.
  const [shareEngineScope, setShareEngineScope] = useState<'all' | 'selected'>('all');
  const [selectedShareEngines, setSelectedShareEngines] = useState<RunnerType[]>([]);
  const [exposedAgentConfigIds, setExposedAgentConfigIds] = useState<string[]>([]);
  const [savedShare, setSavedShare] = useState<DaemonShare | null>(null);
  const [revokingShare, setRevokingShare] = useState<DaemonShare | null>(null);

  const {
    data: projects = [],
    isLoading: isShareProjectsLoading,
    isFetching: isShareProjectsFetching,
  } = useShareableTeamProjects(token, selectedShareTeamId);

  const { data: shareCandidates = [], isLoading: isCandidatesLoading } = useDaemonShareCandidates(
    token,
    daemon?.id ?? '',
    selectedProjectId,
    { enabled: isOpen && Boolean(daemon) && selectedProjectId.length > 0 },
  );

  const resetWizard = useCallback(() => {
    setStep('target');
    setEditingShare(null);
    setSelectedProjectId('');
    setPlanModeOnly(false);
    setShareEngineScope('all');
    setSelectedShareEngines([]);
    setExposedAgentConfigIds([]);
    setSavedShare(null);
    setRevokingShare(null);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSelectedShareTeamId(defaultShareTeamId);
    }
    resetWizard();
  }, [defaultShareTeamId, isOpen, resetWizard]);

  const activeShares = useMemo(
    () => (daemon ? shares.filter((share) => share.daemonId === daemon.id && share.status === 'ACTIVE') : []),
    [daemon, shares],
  );
  const activeProjectIds = useMemo(
    () => new Set(activeShares.filter((share) => share.id !== editingShare?.id).map((share) => share.projectId)),
    [activeShares, editingShare?.id],
  );
  const teamOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: t('shareTeamPlaceholder'), disabled: true },
      ...teams.map((team) => ({
        value: team.id,
        label: team.name,
      })),
    ],
    [teams, t],
  );
  const projectOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: isShareProjectsLoading ? t('shareProjectsLoading') : t('shareProjectPlaceholder') },
      ...projects.map((project) => ({
        value: project.id,
        label: project.name,
        disabled: activeProjectIds.has(project.id),
      })),
    ],
    [activeProjectIds, isShareProjectsLoading, projects, t],
  );
  const shareEngineCandidates = useMemo(() => getShareEngineCandidates(daemon), [daemon]);
  const isShareEngineSelectionMissing = shareEngineScope === 'selected' && selectedShareEngines.length === 0;
  const isSaving = createShareMutation.isPending || updateShareMutation.isPending;
  const daemonLabel = daemon?.label || daemon?.id.slice(0, 8) || '';
  const resolvedAllowedEngines = shareEngineScope === 'selected' ? selectedShareEngines : [];
  const exposedCandidates = shareCandidates.filter((candidate) => exposedAgentConfigIds.includes(candidate.id));
  const savedTargetShare = savedShare ?? editingShare;
  const shareTargetProjectName =
    savedTargetShare?.projectName ?? projects.find((project) => project.id === selectedProjectId)?.name ?? '';

  const handleShareTeamChange = (teamId: string) => {
    setSelectedShareTeamId(teamId);
    setSelectedProjectId('');
    setExposedAgentConfigIds([]);
  };

  const handleProjectChange = (projectId: string) => {
    setSelectedProjectId(projectId);
    // 프로젝트가 바뀌면 후보 집합 자체가 달라지므로 이전 선택을 유지하지 않는다.
    setExposedAgentConfigIds([]);
  };

  const handleShareEngineToggle = (runnerType: RunnerType) => {
    setSelectedShareEngines((previous) =>
      previous.includes(runnerType) ? previous.filter((engine) => engine !== runnerType) : [...previous, runnerType],
    );
  };

  const handleExposedAgentToggle = (agentConfigId: string) => {
    setExposedAgentConfigIds((previous) =>
      previous.includes(agentConfigId) ? previous.filter((id) => id !== agentConfigId) : [...previous, agentConfigId],
    );
  };

  const handleStartEditingShare = (share: DaemonShare) => {
    setEditingShare(share);
    setSavedShare(null);
    setSelectedShareTeamId(share.teamId ?? defaultShareTeamId);
    setSelectedProjectId(share.projectId);
    setPlanModeOnly(share.planModeOnly);
    const allowed = sanitizeSupportedEngines(share.allowedEngines ?? []);
    setShareEngineScope(allowed.length > 0 ? 'selected' : 'all');
    setSelectedShareEngines(allowed);
    setExposedAgentConfigIds(share.exposedAgentConfigIds ?? []);
    setStep('conditions');
  };

  const handleSave = async () => {
    if (!daemon || selectedProjectId.length === 0) {
      return;
    }

    try {
      // 공유 조건과 노출 에이전트를 한 호출로 보낸다(별도 AgentConfig PUT 없음 = 부분 성공 없음).
      const share = editingShare
        ? await updateShareMutation.mutateAsync({
            daemonId: daemon.id,
            shareId: editingShare.id,
            input: {
              planModeOnly,
              allowedEngines: resolvedAllowedEngines,
              exposedAgentConfigIds,
            },
          })
        : await createShareMutation.mutateAsync({
            daemonId: daemon.id,
            input: {
              projectId: selectedProjectId,
              planModeOnly,
              allowedEngines: resolvedAllowedEngines,
              exposedAgentConfigIds,
            },
          });

      setSavedShare(share);
      setStep('result');
      toast.success(editingShare ? t('toastShareUpdated') : t('toastShareCreated'));
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        editingShare ? t('toastShareUpdateFailed') : t('toastShareCreateFailed'),
      );
      if (message) toast.error(message);
    }
  };

  const handleRevoke = async (share: DaemonShare) => {
    if (!daemon) {
      return;
    }

    try {
      await revokeShareMutation.mutateAsync({ daemonId: daemon.id, shareId: share.id });
      setRevokingShare(null);
      if (editingShare?.id === share.id) {
        resetWizard();
      }
      toast.success(t('toastShareRevoked'));
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('toastShareRevokeFailed'));
      if (message) toast.error(message);
    }
  };

  const canGoNext =
    step === 'target'
      ? selectedProjectId.length > 0 && !isShareProjectsFetching
      : step === 'conditions'
        ? !isShareEngineSelectionMissing
        : true;

  const goNext = () => {
    const index = shareWizardSteps.indexOf(step as ShareWizardStep);
    if (index >= 0 && index < shareWizardSteps.length - 1) {
      setStep(shareWizardSteps[index + 1]);
    }
  };

  const goBack = () => {
    const index = shareWizardSteps.indexOf(step as ShareWizardStep);
    // 편집 흐름은 대상이 고정이므로 조건 단계 아래로 내려가지 않는다.
    const minIndex = editingShare ? 1 : 0;
    if (index > minIndex) {
      setStep(shareWizardSteps[index - 1]);
    }
  };

  const agentConfigsDeepLink =
    savedTargetShare?.teamId && savedTargetShare.projectId
      ? `/agent-configs?teamId=${savedTargetShare.teamId}&projectId=${savedTargetShare.projectId}`
      : null;

  return (
    <BaseModal isOpen={isOpen && daemon !== null} onClose={onClose} variant="bottomSheet" size="md">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold">{t('shareModalTitle', { label: daemonLabel })}</h3>
          <p className="text-xs text-[var(--color-fg-muted)]">{t('shareModalDescription')}</p>
        </div>
        <button type="button" className="btn btn-ghost btn-circle btn-sm shrink-0" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="rounded-xl border border-base-300 bg-base-200 p-3">
        {step !== 'result' ? (
          <ol className="mb-3 flex flex-wrap gap-1.5 text-[11px]" aria-label={t('shareWizardStepsLabel')}>
            {shareWizardSteps.map((value, index) => (
              <li
                key={value}
                aria-current={step === value ? 'step' : undefined}
                className={cn(
                  'rounded-full px-2 py-0.5 font-semibold',
                  step === value
                    ? 'bg-primary/10 text-primary'
                    : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                )}
              >
                {index + 1}. {t(`shareStep.${value}`)}
              </li>
            ))}
          </ol>
        ) : null}

        {step === 'target' ? (
          <div className="grid min-w-0 gap-2 sm:grid-cols-2">
            <div className="min-w-0">
              <div className="mb-1 text-[11px] text-[var(--color-fg-subtle)]">{t('shareTeamLabel')}</div>
              <Select
                options={teamOptions}
                value={selectedShareTeamId}
                onChange={handleShareTeamChange}
                size="xs"
                fullWidth
                disabled={teams.length === 0 || isSaving}
              />
            </div>
            <div className="min-w-0">
              <div className="mb-1 text-[11px] text-[var(--color-fg-subtle)]">{t('shareProjectLabel')}</div>
              <Select
                options={projectOptions}
                value={selectedProjectId}
                onChange={handleProjectChange}
                size="xs"
                fullWidth
                disabled={projects.length === 0 || isShareProjectsFetching || isSaving}
              />
            </div>
          </div>
        ) : null}

        {step === 'conditions' ? (
          <>
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2">
              <input
                type="checkbox"
                className="checkbox checkbox-primary checkbox-xs mt-0.5"
                checked={planModeOnly}
                onChange={(event) => setPlanModeOnly(event.target.checked)}
                disabled={isSaving}
              />
              <span className="min-w-0">
                <span className="flex items-center gap-1">
                  <span className="text-xs font-medium text-base-content/80">{t('sharePlanModeOnlyLabel')}</span>
                  <Tooltip
                    content={t('sharePlanModeOnlyTooltip')}
                    placement="top"
                    focusable
                    className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-base-300 text-[9px] font-semibold leading-none text-[var(--color-fg-muted)]"
                  >
                    ?
                  </Tooltip>
                </span>
                <span className="block text-[11px] leading-4 text-[var(--color-fg-muted)]">
                  {t('sharePlanModeOnlyDescription')}
                </span>
              </span>
            </label>
            <fieldset className="mt-3 rounded-lg border border-base-300 bg-base-100 px-3 py-2">
              <legend className="px-1 text-xs font-medium text-base-content/80">{t('shareEngineScopeLabel')}</legend>
              <div className="flex flex-wrap gap-3">
                <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    className="radio radio-primary radio-xs"
                    name="share-engine-scope"
                    value="all"
                    checked={shareEngineScope === 'all'}
                    onChange={() => setShareEngineScope('all')}
                    disabled={isSaving}
                  />
                  <span>{t('shareEngineScopeAll')}</span>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    className="radio radio-primary radio-xs"
                    name="share-engine-scope"
                    value="selected"
                    checked={shareEngineScope === 'selected'}
                    onChange={() => setShareEngineScope('selected')}
                    disabled={isSaving}
                  />
                  <span>{t('shareEngineScopeSelected')}</span>
                </label>
              </div>
              {shareEngineScope === 'selected' ? (
                <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {shareEngineCandidates.map((runnerType) => (
                    <label
                      key={runnerType}
                      className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-2 py-1.5 text-xs"
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-xs"
                        checked={selectedShareEngines.includes(runnerType)}
                        onChange={() => handleShareEngineToggle(runnerType)}
                        disabled={isSaving}
                      />
                      <span>{t(`runnerTypeOption.${runnerType}`)}</span>
                    </label>
                  ))}
                </div>
              ) : null}
              <p className="mt-2 text-[11px] leading-4 text-[var(--color-fg-muted)]">
                {isShareEngineSelectionMissing ? t('shareEngineScopeEmptyWarning') : t('shareEngineScopeHint')}
              </p>
            </fieldset>
          </>
        ) : null}

        {step === 'agents' ? (
          <fieldset className="rounded-lg border border-base-300 bg-base-100 px-3 py-2">
            <legend className="px-1 text-xs font-medium text-base-content/80">{t('shareExposedAgentsLabel')}</legend>
            <p className="mb-2 text-[11px] leading-4 text-[var(--color-fg-muted)]">{t('shareExposedAgentsHint')}</p>
            {isCandidatesLoading ? (
              <div className="flex justify-center py-3">
                <Spinner />
              </div>
            ) : shareCandidates.length === 0 ? (
              <p className="py-2 text-[11px] text-[var(--color-fg-muted)]">{t('shareExposedAgentsEmpty')}</p>
            ) : (
              <div className="grid gap-1.5">
                {shareCandidates.map((candidate) => (
                  <label
                    key={candidate.id}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-base-300 px-2 py-1.5 text-xs"
                  >
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={exposedAgentConfigIds.includes(candidate.id)}
                      onChange={() => handleExposedAgentToggle(candidate.id)}
                      disabled={isSaving}
                    />
                    <span className="min-w-0 flex-1 truncate">{candidate.name}</span>
                  </label>
                ))}
              </div>
            )}
            {exposedAgentConfigIds.length === 0 ? (
              <p className="mt-2 rounded-md bg-warning/10 px-2 py-1.5 text-[11px] leading-4 text-warning">
                {t('shareExposedAgentsEmptyWarning')}
              </p>
            ) : null}
          </fieldset>
        ) : null}

        {step === 'preview' ? (
          <div className="space-y-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-[11px] leading-5">
            <p className="text-xs font-medium text-base-content/80">{t('sharePreviewTitle')}</p>
            <p className="text-[var(--color-fg-muted)]">{t('sharePreviewOwnershipNotice')}</p>
            <p>
              <span className="text-[var(--color-fg-subtle)]">{t('sharePreviewProject')}</span>{' '}
              <span className="font-medium">{shareTargetProjectName}</span>
            </p>
            <p>
              <span className="text-[var(--color-fg-subtle)]">{t('sharePreviewPlanMode')}</span>{' '}
              <span className="font-medium">{planModeOnly ? t('shareModePlanOnly') : t('shareModeFull')}</span>
            </p>
            <p>
              <span className="text-[var(--color-fg-subtle)]">{t('sharePreviewEngines')}</span>{' '}
              <span className="font-medium">
                {resolvedAllowedEngines.length === 0
                  ? t('shareEnginesAllBadge')
                  : resolvedAllowedEngines.map((engine) => t(`runnerTypeOption.${engine}`)).join(', ')}
              </span>
            </p>
            <p>
              <span className="text-[var(--color-fg-subtle)]">{t('sharePreviewAgents')}</span>{' '}
              <span className="font-medium">
                {exposedCandidates.length === 0
                  ? t('shareExposedAgentsNone')
                  : exposedCandidates.map((candidate) => candidate.name).join(', ')}
              </span>
            </p>
            {exposedAgentConfigIds.length === 0 ? (
              <p className="rounded-md bg-warning/10 px-2 py-1.5 text-warning">{t('shareExposedAgentsEmptyWarning')}</p>
            ) : null}
          </div>
        ) : null}

        {step === 'result' ? (
          <div className="space-y-2 rounded-lg border border-base-300 bg-base-100 px-3 py-2 text-[11px] leading-5">
            <p className="text-xs font-medium text-base-content/80">{t('shareResultTitle')}</p>
            <p className="text-[var(--color-fg-muted)]">{t('sharePreviewOwnershipNotice')}</p>
            <p>
              {savedShare?.exposedAgentConfigIds && savedShare.exposedAgentConfigIds.length > 0
                ? t('shareResultExposed', { count: savedShare.exposedAgentConfigIds.length })
                : t('shareResultNotExposed')}
            </p>
            <div className="flex flex-wrap gap-3 pt-1">
              <button
                type="button"
                className="link text-primary"
                onClick={() => {
                  resetWizard();
                }}
              >
                {t('shareResultBackToAccountScope')}
              </button>
              {agentConfigsDeepLink ? (
                <a className="link text-primary" href={agentConfigsDeepLink}>
                  {t('shareResultOpenProjectExecutionConfigs')}
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {step === 'result' ? null : (
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={goBack}
              disabled={step === 'target' || (editingShare !== null && step === 'conditions') || isSaving}
            >
              {tCommon('actions.back')}
            </button>
            {step === 'preview' ? (
              <button
                type="button"
                className="btn btn-primary btn-xs"
                onClick={() => void handleSave()}
                disabled={isSaving || selectedProjectId.length === 0}
              >
                {isSaving ? <Spinner /> : editingShare ? tCommon('actions.save') : tCommon('actions.share')}
              </button>
            ) : (
              <button type="button" className="btn btn-primary btn-xs" onClick={goNext} disabled={!canGoNext}>
                {tCommon('actions.next')}
              </button>
            )}
          </div>
        )}

        {teams.length === 0 ? (
          <p className="mt-2 text-xs text-[var(--color-fg-muted)]">{t('shareTeamsEmpty')}</p>
        ) : selectedShareTeamId && projects.length === 0 && !isShareProjectsLoading ? (
          <p className="mt-2 text-xs text-[var(--color-fg-muted)]">{t('shareProjectsEmpty')}</p>
        ) : null}
      </div>

      <div className="mt-4">
        <div className="mb-2 text-xs font-medium text-[var(--color-fg-subtle)]">{t('sharedProjectsTitle')}</div>
        <div className="space-y-2">
          {activeShares.length > 0 ? (
            activeShares.map((share) => {
              const allowedEngines = sanitizeSupportedEngines(share.allowedEngines ?? []);
              const visibleEngines = allowedEngines.slice(0, shareEngineBadgeVisibleCount);
              const hiddenEngineCount = allowedEngines.length - visibleEngines.length;

              return (
                <div key={share.id} className="min-w-0 rounded-lg border border-base-300 px-3 py-2">
                  <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2">
                    <div className="min-w-0">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <span className="truncate text-sm font-medium text-base-content/80">
                          {share.projectName ?? share.projectId.slice(0, 8)}
                        </span>
                        <span
                          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${share.planModeOnly ? 'bg-primary/10 text-primary' : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]'}`}
                        >
                          {share.planModeOnly ? t('shareModePlanOnly') : t('shareModeFull')}
                        </span>
                        {allowedEngines.length === 0 ? (
                          <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-neutral-fg)]">
                            {t('shareEnginesAllBadge')}
                          </span>
                        ) : (
                          <>
                            {visibleEngines.map((runnerType) => (
                              <span
                                key={runnerType}
                                className="shrink-0 rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold text-secondary"
                              >
                                {t(`runnerTypeOption.${runnerType}`)}
                              </span>
                            ))}
                            {hiddenEngineCount > 0 ? (
                              <span className="shrink-0 rounded-full bg-secondary/10 px-2 py-0.5 text-[10px] font-semibold text-secondary">
                                {t('shareEnginesMore', { count: hiddenEngineCount })}
                              </span>
                            ) : null}
                          </>
                        )}
                        <span className="shrink-0 rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-neutral-fg)]">
                          {share.exposedAgentConfigIds.length > 0
                            ? t('shareExposedCount', { count: share.exposedAgentConfigIds.length })
                            : t('shareExposedNoneBadge')}
                        </span>
                      </div>
                      <div className="mt-0.5 truncate text-[11px] text-[var(--color-fg-subtle)]">
                        {share.teamName ?? t('shareTeamUnknown')}
                      </div>
                      {share.teamId ? (
                        <a
                          className="link mt-1 inline-block text-[11px] text-primary"
                          href={`/agent-configs?teamId=${share.teamId}&projectId=${share.projectId}`}
                        >
                          {t('shareProjectAgentConfigsLink')}
                        </a>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => handleStartEditingShare(share)}
                        disabled={updateShareMutation.isPending}
                      >
                        {t('shareEdit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => setRevokingShare(share)}
                        disabled={revokeShareMutation.isPending}
                      >
                        {t('shareRevoke')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="rounded-lg border border-dashed border-base-300 px-3 py-4 text-center text-xs text-[var(--color-fg-muted)]">
              {t('sharedProjectsEmpty')}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={revokingShare !== null}
        title={t('shareRevokeConfirmTitle')}
        message={t('shareRevokeConfirmMessage', { project: revokingShare?.projectName ?? '' })}
        confirmLabel={t('shareRevoke')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={revokeShareMutation.isPending}
        onConfirm={() => {
          if (revokingShare) {
            void handleRevoke(revokingShare);
          }
        }}
        onCancel={() => setRevokingShare(null)}
      />
    </BaseModal>
  );
};

/**
 * 러너 등록·편집·정렬·공유 관리 본문. 사용자 계정 범위 자산이므로 프로젝트 선택과 무관하게 동작한다.
 * 계정 범위 `내 실행 환경` 모달의 러너 섹션이 이 본문을 그대로 쓴다.
 */
export const DaemonManagerPanel = ({ token, teams, currentTeamId, isActive = true }: DaemonManagerPanelProps) => {
  const t = useTranslations('components.daemonManageModal');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const {
    data: daemonsResult,
    isLoading,
    refetch: refetchDaemons,
  } = useDaemons(token, {
    refetchInterval: isActive ? daemonManageRefetchIntervalMs : false,
  });
  const daemons = useMemo(() => daemonsResult?.daemons ?? [], [daemonsResult?.daemons]);
  const onlineThresholdMs = daemonsResult?.onlineThresholdMs ?? DEFAULT_DAEMON_ONLINE_THRESHOLD_MS;
  const { data: daemonShares = [] } = useDaemonShares(token);
  const deleteMutation = useDeleteDaemonMutation(token);
  const updateMutation = useUpdateDaemonMutation(token);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingDaemon, setEditingDaemon] = useState<Daemon | null>(null);
  const [sharingDaemon, setSharingDaemon] = useState<Daemon | null>(null);
  const [deletingDaemon, setDeletingDaemon] = useState<Daemon | null>(null);
  const [orderedDaemons, setOrderedDaemons] = useState<Daemon[]>([]);
  const [draggingDaemonId, setDraggingDaemonId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);
  const [statusNow, setStatusNow] = useState(() => Date.now());
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    if (!isActive) {
      setIsCreateModalOpen(false);
      setEditingDaemon(null);
      setSharingDaemon(null);
      setDeletingDaemon(null);
      setDraggingDaemonId(null);
    }
  }, [isActive]);

  useEffect(() => {
    if (isActive && draggingDaemonId === null && !isReordering) {
      setOrderedDaemons(daemons);
    }
  }, [daemons, draggingDaemonId, isActive, isReordering]);

  useEffect(() => {
    if (!isActive) return;
    setStatusNow(Date.now());
    void refetchDaemons();
  }, [isActive, refetchDaemons]);

  useEffect(() => {
    if (!isActive) return;
    setStatusNow(Date.now());
    const intervalId = window.setInterval(() => setStatusNow(Date.now()), daemonStatusTickMs);
    return () => window.clearInterval(intervalId);
  }, [isActive]);

  const handleCopyManualUpdateCommand = async (packageName: string, version: string) => {
    try {
      await copyTextToClipboard(buildManualUpdateCommand(packageName, version));
      toast.success(t('toastCopied'));
    } catch {
      toast.error(tRoot('uiErrors.copyFailed'));
    }
  };

  const handleDeleteDaemon = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('toastDeleted'));
      setDeletingDaemon(null);
    } catch (error) {
      const message = resolveApiErrorToastMessageOrFallback(
        tRoot,
        error,
        tRoot('uiErrors.deleteFailed', { target: tRoot('uiErrors.targets.runner') }),
      );
      if (message) toast.error(message);
    }
  };

  const persistDaemonOrder = async (nextDaemons: Daemon[]) => {
    const changedDaemons = nextDaemons
      .map((daemon, index) => ({ daemon, sortOrder: index }))
      .filter(({ daemon, sortOrder }) => daemon.sortOrder !== sortOrder);

    if (changedDaemons.length === 0) {
      return;
    }

    setIsReordering(true);
    try {
      await Promise.all(
        changedDaemons.map(({ daemon, sortOrder }) =>
          updateMutation.mutateAsync({
            id: daemon.id,
            input: { sortOrder },
          }),
        ),
      );
      toast.success(t('toastOrderUpdated'));
    } catch (error) {
      setOrderedDaemons(daemons);
      const message = resolveApiErrorToastMessageOrFallback(tRoot, error, t('toastOrderUpdateFailed'));
      if (message) toast.error(message);
    } finally {
      setIsReordering(false);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDraggingDaemonId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggingDaemonId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = orderedDaemons.findIndex((daemon) => daemon.id === active.id);
    const newIndex = orderedDaemons.findIndex((daemon) => daemon.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const nextDaemons = arrayMove(orderedDaemons, oldIndex, newIndex);
    setOrderedDaemons(nextDaemons);
    void persistDaemonOrder(nextDaemons);
  };

  return (
    <>
      <div>
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-base-300 bg-base-200 p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">{t('registered')}</p>
            <p className="text-xs text-[var(--color-fg-muted)]">{t('tokenOneTimeNotice')}</p>
          </div>
          <button
            type="button"
            className="btn btn-primary btn-sm shrink-0"
            onClick={() => {
              setEditingDaemon(null);
              setIsCreateModalOpen(true);
            }}
          >
            {t('create')}
          </button>
        </div>

        <div>
          {isLoading ? (
            <div className="space-y-2">
              <div className="skeleton h-10 w-full" />
              <div className="skeleton h-10 w-full" />
            </div>
          ) : orderedDaemons.length === 0 ? (
            <div className="rounded-lg border border-base-300 p-3 text-sm text-[var(--color-fg-muted)]">
              {t('empty')}
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={() => setDraggingDaemonId(null)}
            >
              <SortableContext items={orderedDaemons.map((daemon) => daemon.id)} strategy={verticalListSortingStrategy}>
                <ul className="space-y-2">
                  {orderedDaemons.map((daemon) => {
                    const orderedSupportedEngines = runnerTypeValues.filter((runnerType) =>
                      daemon.supportedEngines.includes(runnerType),
                    );
                    const isOrderSaving = isReordering || updateMutation.isPending;
                    const activeShares = daemonShares.filter(
                      (share) => share.daemonId === daemon.id && share.status === 'ACTIVE',
                    );
                    const isOnline = isDaemonOnline(daemon.lastSeenAt, onlineThresholdMs, statusNow);
                    // 패키지를 모르는 과거 레코드는 러너 실패로 본다(그때는 러너만 보고했다).
                    const failedPackage = daemon.updateFailedPackage === 'cli' ? 'cli' : 'runner';
                    // 러너 실패만 러너 버전으로 자동 숨김을 판정한다. CLI 버전은 축이 달라 비교할 수 없으므로
                    // 서버의 명시적 해제(성공 보고·하트비트)만 따른다.
                    const updateFailure =
                      daemon.updateFailedVersion &&
                      (failedPackage === 'cli' ||
                        shouldShowRunnerUpdateFailure(daemon.runnerVersion, daemon.updateFailedVersion))
                        ? {
                            package: failedPackage,
                            packageName: runnerUpdatePackageNames[failedPackage],
                            version: daemon.updateFailedVersion,
                            reason: daemon.updateFailedReason ?? 'UNKNOWN',
                          }
                        : null;

                    return (
                      <SortableDaemonListItem
                        key={daemon.id}
                        daemon={daemon}
                        isDisabled={isOrderSaving || deleteMutation.isPending}
                        isDraggingActive={draggingDaemonId !== null}
                        dragLabel={t('dragHandleAriaLabel', { label: daemon.label || daemon.id.slice(0, 8) })}
                      >
                        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <div
                                className="inline-flex min-w-0 max-w-full items-center gap-1.5 truncate text-sm font-semibold"
                                title={daemon.label || daemon.id.slice(0, 8)}
                              >
                                {daemon.osType ? (
                                  <span aria-hidden="true" className="shrink-0">
                                    {getOsTypeIcon(daemon.osType)}
                                  </span>
                                ) : null}
                                <span className="truncate">{daemon.label || daemon.id.slice(0, 8)}</span>
                              </div>
                              <Tooltip
                                content={formatDate(daemon.lastSeenAt)}
                                placement="top"
                                focusable
                                className="inline-flex shrink-0 items-center"
                              >
                                <span
                                  className={cn(
                                    'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                                    isOnline
                                      ? 'bg-success/15 text-success'
                                      : 'bg-[var(--chip-neutral-bg)] text-[var(--chip-neutral-fg)]',
                                  )}
                                >
                                  {isOnline ? t('running') : t('offline')}
                                </span>
                              </Tooltip>
                            </div>
                            <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[var(--color-fg-subtle)]">
                              <span className="inline-flex items-center gap-1">
                                <span>{t('runnerVersionValue')}</span>
                                <span className="font-mono text-base-content/80">
                                  {formatRunnerVersion(daemon.runnerVersion)}
                                </span>
                              </span>
                              {/* 자동 업데이트 차단은 사용자가 직접 조치해야 풀리므로 재시작 대기 배지보다 우선한다. */}
                              {updateFailure ? (
                                <span className="rounded-full bg-error/15 px-2 py-0.5 text-[10px] font-semibold text-error">
                                  {updateFailure.package === 'cli'
                                    ? t('updateBlockedBadgeCli')
                                    : `${formatRunnerVersion(updateFailure.version)} ${t('updateBlockedBadge')}`}
                                </span>
                              ) : shouldShowPendingRunnerVersion(daemon.runnerVersion, daemon.pendingVersion) ? (
                                <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[10px] font-semibold text-warning">
                                  {formatRunnerVersion(daemon.pendingVersion)} {t('pendingRestart')}
                                </span>
                              ) : null}
                              <RunnerEngineIcons engines={orderedSupportedEngines} />
                              {/* 칩은 러너/모델 섹션 공통으로 text-[10px] font-semibold 스케일을 쓴다. */}
                              <span className="rounded-full bg-[var(--chip-neutral-bg)] px-2 py-0.5 text-[10px] font-semibold text-[var(--chip-neutral-fg)]">
                                {t('shareCount', { count: activeShares.length })}
                              </span>
                            </div>
                            {updateFailure ? (
                              <div className="mt-2 rounded-lg bg-error/10 px-3 py-2 text-xs text-base-content/80">
                                <p>
                                  {updateFailure.reason === 'PERMISSION_DENIED'
                                    ? t('updateBlockedPermission', {
                                        packageName: updateFailure.packageName,
                                        version: formatRunnerVersion(updateFailure.version),
                                      })
                                    : t('updateBlockedUnknown', {
                                        packageName: updateFailure.packageName,
                                        version: formatRunnerVersion(updateFailure.version),
                                      })}
                                </p>
                                {/* 버전이 semver 형식일 때만 명령을 만든다 — 보고된 값이 셸 명령에 그대로 섞이지 않게 한다. */}
                                {isSafeInstallVersion(updateFailure.version) ? (
                                  <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-2">
                                    <code className="min-w-0 break-all rounded bg-base-200 px-2 py-1 font-mono text-[11px]">
                                      {buildManualUpdateCommand(updateFailure.packageName, updateFailure.version)}
                                    </code>
                                    <button
                                      type="button"
                                      className="btn btn-ghost btn-xs px-2"
                                      onClick={() =>
                                        handleCopyManualUpdateCommand(updateFailure.packageName, updateFailure.version)
                                      }
                                      aria-label={t('updateBlockedCommand')}
                                    >
                                      {tCommon('actions.copy')}
                                    </button>
                                  </div>
                                ) : null}
                                <p className="mt-1.5">
                                  {updateFailure.package === 'cli'
                                    ? t('updateBlockedResolveHintCli')
                                    : t('updateBlockedResolveHintRunner')}
                                </p>
                                {daemon.updateFailedMessage ? (
                                  <pre className="mt-1.5 max-h-24 overflow-auto whitespace-pre-wrap break-all font-mono text-[11px] text-[var(--color-fg-muted)]">
                                    {daemon.updateFailedMessage}
                                  </pre>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs px-2"
                              onClick={() => setSharingDaemon(daemon)}
                              disabled={deleteMutation.isPending || isOrderSaving}
                              aria-label={t('shareManageAriaLabel', { label: daemon.label || daemon.id.slice(0, 8) })}
                            >
                              {tCommon('actions.share')}
                            </button>
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs px-2"
                              onClick={() => {
                                setEditingDaemon(daemon);
                                setIsCreateModalOpen(true);
                              }}
                              disabled={deleteMutation.isPending || isOrderSaving}
                            >
                              {tCommon('actions.edit')}
                            </button>
                            {isOnline ? <DaemonRestartButton token={token} daemonId={daemon.id} /> : null}
                            <button
                              type="button"
                              className="btn btn-ghost btn-xs px-2 text-error"
                              onClick={() => setDeletingDaemon(daemon)}
                              disabled={deleteMutation.isPending || isOrderSaving}
                            >
                              {tCommon('actions.delete')}
                            </button>
                          </div>
                        </div>
                      </SortableDaemonListItem>
                    );
                  })}
                </ul>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <DaemonCreateModal
        isOpen={isActive && isCreateModalOpen}
        token={token}
        daemon={editingDaemon}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditingDaemon(null);
        }}
      />
      <DaemonShareModal
        isOpen={isActive && sharingDaemon !== null}
        token={token}
        daemon={sharingDaemon}
        teams={teams}
        currentTeamId={currentTeamId}
        shares={daemonShares}
        onClose={() => setSharingDaemon(null)}
      />
      <DeleteConfirmModal
        isOpen={Boolean(deletingDaemon)}
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage')}
        confirmLabel={t('deleteConfirmAction')}
        cancelLabel={tCommon('actions.cancel')}
        isPending={deleteMutation.isPending}
        onConfirm={() => {
          if (deletingDaemon) {
            void handleDeleteDaemon(deletingDaemon.id);
          }
        }}
        onCancel={() => setDeletingDaemon(null)}
      />
    </>
  );
};
